import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import PieChart from '../charts/pieChart';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';

interface WaiverReportSummaryProps {
  summary: {
    waiversByAirline: { [key: string]: number };
    mostAffectedAirports: string[];
    tripsMonitored: number;
    waiversIssued: number;
    tripsMatchingWaivers: number;
    percentageTripsMatchingWaivers: number;
  };
}

interface ChartData {
  title: string;
  categories: string[];
  series: {
    name: string;
    data: number[];
  }[];
}

const WaiverReportSummary: React.FC<WaiverReportSummaryProps> = ({ summary }) => {
  const [waiversByAirline, setWaiversByAirline] = useState<ChartData | null>(null);
  const [leftWaiverAirports, setLeftWaiverAirports] = useState<string[]>([]);
  const [rightWaiverAirports, setRightWaiverAirports] = useState<string[]>([]);

  useEffect(() => {
    buildWaiversByAirline();
    buildWaiverAirports();
  }, [summary]);

  const buildWaiversByAirline = () => {
    if (_.isEmpty(summary.waiversByAirline)) {
      return;
    }

    const chartData: ChartData = {
      title: 'Waivers by Airline',
      categories: [],
      series: [
        {
          name: 'Waivers by Airline',
          data: [],
        },
      ],
    };
    _.forEach(summary.waiversByAirline, (count, airline) => {
      chartData.categories.push(airline);
      chartData.series[0].data.push(count);
    });
    setWaiversByAirline(chartData);
  };

  const buildWaiverAirports = () => {
    const airports = summary.mostAffectedAirports || [];
    const maxAirports = Math.min(airports.length, 10);
    const prettyAirports = airports.slice(0, maxAirports).map((airport, index) => `${index + 1}. ${airport}`);

    const leftAirportsLength = Math.round(maxAirports / 2);
    setLeftWaiverAirports(_.take(prettyAirports, leftAirportsLength));

    const rightAirportsLength = maxAirports - leftAirportsLength;
    setRightWaiverAirports(_.takeRight(prettyAirports, rightAirportsLength));
  };

  const hasTripsMonitored = () => {
    return _.isNumber(summary.tripsMonitored);
  };

  return (
    <div className="report-results">
      <div className="col-md-1"></div>
      <div className="col-md-3 box-container">
      <SummaryBox
          label="Trips Monitored"
          value={hasTripsMonitored() ? summary.tripsMonitored.toLocaleString() : <img width="36" height="36" src="/images/spinner.gif" alt="Loading" />}
          tooltip="Unique monitored trips that are in the selected Trip Groups and also include travel dates that are either inside or spanning the specified report date range."
        />
        <SummaryBox
          label="Total Waivers Issued"
          value={summary.waiversIssued.toLocaleString()}
          tooltip="All participating airline issued waivers that include effective travel dates inside the specified report date range."
        />
        <SummaryBox
          label="Total Trips Matching Waivers"
          value={summary.tripsMatchingWaivers.toLocaleString()}
          tooltip="Unique Trips that historically matched waivers for effective travel dates inside the specified report date range."
        />
      </div>
      <div className="col-md-3 box-container">
      <SummaryBox
          label="Top 10 Airports With Waivers"
          value={
            <div className="box-value row">
              <div className="col-md-6">
                {leftWaiverAirports.map((airport, index) => (
                  <div key={index}>{airport}</div>
                ))}
              </div>
              <div className="col-md-6">
                {rightWaiverAirports.map((airport, index) => (
                  <div key={index}>{airport}</div>
                ))}
              </div>
            </div>
          }
          tooltip="List of airports in order of the number of trips matching waivers for the airport. Results are based on the Trip Groups and date range specified for this report."
          summaryClass='double-summary-box'
          boxValueClass='box-value row'
        />
        <SummaryBox
          label="% Trips Matching Waivers"
          value={hasTripsMonitored() ? `${summary.percentageTripsMatchingWaivers}%` : <img width="36" height="36" src="/images/spinner.gif" alt="Loading" />}
          tooltip="Percentage of trips that matched waivers for effective travel dates inside the specified report date range."
        />
      </div>
      <div className="col-md-4 chart-container">
        <div className="chart-title">Waivers by Airline</div>
        <div className="col-md-12 data-chart" id="waiversByAirline">
          {waiversByAirline && (
            <PieChart
              reportData={waiversByAirline}
              radius={120}
              height={365}
              includeLegend={true}
            />
          )}
        </div>
      </div>
      <div className="col-md-1"></div>
    </div>
  );
};

interface SummaryBoxProps {
  label: string;
  value: React.ReactNode;
  tooltip: string;
  summaryClass?: string;
  boxValueClass?: string;
}

const SummaryBox: React.FC<SummaryBoxProps> = ({ label, value, tooltip, summaryClass='summary-box', boxValueClass='box-value' }) => (
  <OverlayTrigger delay={{ hide: 450, show: 300 }} placement="top"
    overlay={(props) => (
      <Tooltip {...props} className="overlay-tooltip">
        {tooltip}
      </Tooltip>
    )}>
    <div className={`col-md-12 data-point ${summaryClass}`}>
      <div className="box-label">{label}</div>
      <div className={boxValueClass}>{value}</div>
    </div>
  </OverlayTrigger>
);

export default WaiverReportSummary;